import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {
    HStack,
    Box,
    Text,
    Radio,
    List,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

/**
 * RadioSortingOption is a React functional component that represents a sorting option
 * in the form of a radio button.
 *
 * @param {Object} props - The properties object.
 * @param {number} props.sortingIdx - The index of the sorting option, used for unique identification.
 * @param {string} props.sortingLabel - The label for the sorting option, displayed to the user.
 * @param {string} props.sortingHref - The URL to navigate to when the sorting option is selected.
 * @param {boolean} [props.isSelected=false] - A flag indicating whether this sorting option is currently selected.
 *
 * @returns {JSX.Element} A JSX element representing a sorting option with a radio button and label.
 */
const RadioSortingOption = ({sortingIdx, sortingLabel, sortingHref, isSelected = false}) => {
    const history = useHistory()

    return (
        <HStack gap={0}>
            <Radio
                id={`radio-sorting-label-${sortingIdx}`}
                isChecked={isSelected}
                onChange={() => !isSelected && history.push(sortingHref)}
            >
                {sortingLabel}
            </Radio>
        </HStack>
    )
}

RadioSortingOption.propTypes = {
    sortingIdx: PropTypes.number.isRequired,
    sortingLabel: PropTypes.string.isRequired,
    sortingHref: PropTypes.string.isRequired,
    isSelected: PropTypes.bool
}

/**
 * RadioSortingOptions is a React functional component that renders a list of radio button options
 * for sorting items.
 *
 * This component is memoized using React.memo, meaning it will only re-render when its props change.
 * The props are used solely for data representation.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.sortUrls - An array of URLs corresponding to each sorting option. Each URL
 *     represents a link to the sorted list of products.
 * @param {Array} props.sortingOptions - An array of sorting option objects. Each object should
 *     contain an `id` and a `label` property, where `id` is a unique identifier for the sorting
 *     option and `label` is the display text for the option.
 * @param {Object} props.selectedSortingOptionLabel - The currently selected sorting option object.
 *     This object should contain a `label` property that matches one of the labels in the
 *     `sortingOptions` array, indicating the currently active sorting method.
 *
 * @returns {JSX.Element|null} A JSX element containing a fieldset with a list of radio buttons for
 *     sorting options, or null if no sorting URLs are provided.
 */
const RadioSortingOptions = ({sortUrls = [], sortingOptions = [], selectedSortingOptionLabel}) => {
    const {formatMessage} = useIntl()

    return (
        sortUrls.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {formatMessage({
                        id: 'refinements.sorting.legend',
                        defaultMessage: 'Possible sorting options'
                    })}
                </legend>

                <List display="flex" flexDirection="column" gap={10}>
                    {sortUrls.map((href, idx) => (
                        <ListItem key={sortingOptions[idx]?.id} margin={0}>
                            <RadioSortingOption
                                sortingIdx={idx}
                                sortingLabel={sortingOptions[idx]?.label}
                                sortingHref={href}
                                isSelected={
                                    selectedSortingOptionLabel?.label === sortingOptions[idx]?.label
                                }
                                selectedSortingOptionLabel={selectedSortingOptionLabel?.label}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        )
    )
}

RadioSortingOptions.propTypes = {
    sortUrls: PropTypes.arrayOf(PropTypes.string),
    sortingOptions: PropTypes.arrayOf(PropTypes.object),
    selectedSortingOptionLabel: PropTypes.object
}

// Memoized RadioSortingOptions to prevent unnecessary re-renders.
// Use case: accordion open / close with RadioSortingOptions
export default React.memo(RadioSortingOptions)
